'use client';

import { useRef, useEffect, useState } from 'react';
import { motion, useScroll, useTransform, useSpring, useInView } from 'framer-motion';

const easing = [0.22, 1, 0.36, 1] as const;

const stats = [
  { value: 3, suffix: 'h', label: 'Tempo medio di prima risposta del team', decimals: 0 },
  { value: 80, suffix: '%', label: 'Delle attività ripetitive automatizzate dal day one', decimals: 0 },
  { value: 3, suffix: 'sett.', label: 'Dal brief al primo rilascio in produzione', decimals: 0 },
];

function useCounter(target: number, isActive: boolean, duration = 1400, decimals = 0) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!isActive) return;
    let rafId: number;
    let startTime: number | null = null;
    const factor = Math.pow(10, decimals);
    const tick = (ts: number) => {
      if (!startTime) startTime = ts;
      const p = Math.min((ts - startTime) / duration, 1);
      const e = 1 - Math.pow(1 - p, 3);
      setValue(Math.round(e * target * factor) / factor);
      if (p < 1) rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [isActive, target, duration, decimals]);
  return value;
}

function StatItem({ stat, index }: { stat: (typeof stats)[0]; index: number }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-15% 0px' });
  const count = useCounter(stat.value, isInView, 1600, stat.decimals);

  return (
    <motion.div
      ref={ref}
      className="group relative flex flex-col justify-between py-10 px-8 border-r border-white/[0.06] last:border-r-0"
      initial={{ opacity: 0, y: 32 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.65, delay: index * 0.1, ease: easing }}
    >
      {/* Hover reveal glow */}
      <div className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700"
        style={{ background: 'radial-gradient(ellipse 100% 80% at 50% 100%, rgba(230,43,54,0.06) 0%, transparent 70%)' }} />

      {/* Animated top border */}
      <motion.div
        className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/50 to-transparent"
        initial={{ scaleX: 0 }}
        animate={isInView ? { scaleX: 1 } : {}}
        transition={{ duration: 1, delay: 0.3 + index * 0.1, ease: easing }}
      />

      <div className="relative z-10">
        <div className="flex items-baseline gap-1 mb-3">
          <span className="text-6xl lg:text-7xl font-black tracking-tighter text-white tabular-nums">
            {count}
          </span>
          {stat.suffix && (
            <span className="text-3xl lg:text-4xl font-black text-primary">{stat.suffix}</span>
          )}
        </div>
        <p className="text-sm leading-6 text-gray-500 group-hover:text-gray-400 transition-colors duration-300">
          {stat.label}
        </p>
      </div>
    </motion.div>
  );
}

export default function StatsSection() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const y = useSpring(useTransform(scrollYProgress, [0, 1], [40, -40]), { stiffness: 50, damping: 18 });

  return (
    <section ref={ref} className="relative w-full overflow-hidden bg-gray-950 border-t border-white/[0.05]">
      {/* Parallax background radial */}
      <motion.div
        className="pointer-events-none absolute inset-0"
        style={{ y }}
      >
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-[60vw] w-[60vw] rounded-full bg-primary/[0.04] blur-[120px]" />
      </motion.div>

      <div className="relative mx-auto max-w-7xl px-6 lg:px-8">
        <div className="grid grid-cols-2 divide-y divide-white/[0.06] lg:grid-cols-4 lg:divide-y-0 border-b border-white/[0.06]">
          {stats.map((stat, i) => (
            <StatItem key={stat.label} stat={stat} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
